import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Breadcrumbs — token-driven navigation trail.
 *
 * Accepts an ordered list of items:
 *   items = [
 *     { label: "Admin", to: "/admin" },
 *     { label: "Users", to: "/admin/users" },
 *     { label: "Detail" },  // last item — current page, no `to`
 *   ]
 *
 * a11y contract:
 *   * `<nav aria-label="Breadcrumb">`
 *   * Last item has `aria-current="page"` and is not a link.
 *   * Separators are decorative (`aria-hidden`).
 */

export function Breadcrumbs({ items = [], className, "data-testid": testId }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid={testId}
      className={cn("text-[13px] text-[var(--ds-color-ink-muted)]", className)}
    >
      <ol className="flex items-center flex-wrap gap-1">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          const Comp = !isLast && item.to ? Link : "span";
          const compProps = !isLast && item.to ? { to: item.to } : {};
          return (
            <li key={`${item.label}-${idx}`} className="inline-flex items-center gap-1">
              {idx > 0 ? (
                <ChevronRight aria-hidden className="w-3 h-3 text-[var(--ds-color-ink-subtle)]" />
              ) : null}
              <Comp
                {...compProps}
                aria-current={isLast ? "page" : undefined}
                className={cn(
                  isLast
                    ? "text-[var(--ds-color-ink)] font-medium"
                    : "hover:text-[var(--ds-color-ink)] hover:underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-[var(--ds-color-accent)] rounded",
                )}
                data-testid={isLast ? `breadcrumb-current` : `breadcrumb-link-${idx}`}
              >
                {item.label}
              </Comp>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
